import { Badge } from '@buyerflo/ui/components/Badge'
import { Card } from '@buyerflo/ui/components/Card'
import { useQuery } from '@tanstack/react-query'
import Button from 'components/button'
import Loading from 'components/Loading'
import { AddIcon } from 'components/icons'
import { affiliateStatusTone, fmtAud } from 'affiliate/shared'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SizableText, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'
import EditAffiliateModal from './EditAffiliateModal'

const STATUSES = ['active', 'paused', 'disabled', 'archived'] as const

const StatTile = ({ label, value }: { label: string; value: string }) => (
    <Card
        padded
        flex={1}
        minWidth={160}
    >
        <SizableText
            fontSize={11}
            textTransform="uppercase"
            letterSpacing={0.4}
            color="$colorMuted"
        >
            {label}
        </SizableText>
        <SizableText
            fontSize={24}
            fontWeight="700"
        >
            {value}
        </SizableText>
    </Card>
)

export default function AffiliatesList() {
    const trpc = useTRPC()
    const navigate = useNavigate()
    const query = useQuery(trpc.affiliateAdmin.listAffiliates.queryOptions())
    const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set())
    const [creating, setCreating] = useState(false)

    if (query.isLoading) return <Loading />
    if (!query.data) return <SizableText>Could not load affiliates.</SizableText>

    const { affiliates, totals } = query.data
    const filtered = statusFilter.size
        ? affiliates.filter(a => statusFilter.has(a.status))
        : affiliates

    const toggle = (s: string) =>
        setStatusFilter(prev => {
            const next = new Set(prev)
            next.has(s) ? next.delete(s) : next.add(s)
            return next
        })

    return (
        <YStack gap="$4">
            <XStack
                alignItems="center"
                gap="$3"
            >
                <SizableText
                    fontSize="$7"
                    fontWeight="700"
                    flex={1}
                >
                    Affiliates
                </SizableText>
                <Button
                    icon={<AddIcon size={16} />}
                    onPress={() => setCreating(true)}
                >
                    Add affiliate
                </Button>
            </XStack>

            {/* Org-wide totals */}
            <XStack
                gap="$3"
                flexWrap="wrap"
            >
                <StatTile
                    label="Total paid to date"
                    value={fmtAud(totals.totalPaidToDateCents)}
                />
                <StatTile
                    label="Total unpaid (owed)"
                    value={fmtAud(totals.totalUnpaidCents)}
                />
                <StatTile
                    label="Affiliates"
                    value={String(affiliates.length)}
                />
            </XStack>

            {/* Status filter chips */}
            <XStack
                gap="$2"
                flexWrap="wrap"
            >
                {STATUSES.map(s => (
                    <View
                        key={s}
                        cursor="pointer"
                        opacity={statusFilter.size === 0 || statusFilter.has(s) ? 1 : 0.4}
                        onPress={() => toggle(s)}
                    >
                        <Badge tone={affiliateStatusTone[s]}>{s}</Badge>
                    </View>
                ))}
            </XStack>

            {/* Table */}
            <Card inset>
                <XStack
                    paddingHorizontal="$4"
                    paddingVertical="$2.5"
                    borderBottomWidth={1}
                    borderBottomColor="$borderColor"
                    gap="$3"
                >
                    <SizableText flex={2} size="$1" color="$colorMuted">Name</SizableText>
                    <SizableText flex={1.5} size="$1" color="$colorMuted" display="none" $sm={{ display: 'flex' }}>Code</SizableText>
                    <SizableText flex={1} size="$1" color="$colorMuted" display="none" $sm={{ display: 'flex' }}>Referrals</SizableText>
                    <SizableText flex={1.3} size="$1" color="$colorMuted">Paid</SizableText>
                    <SizableText flex={1.3} size="$1" color="$colorMuted">Unpaid</SizableText>
                    <SizableText flex={1.2} size="$1" color="$colorMuted">Status</SizableText>
                </XStack>

                {filtered.length === 0 && (
                    <View padding="$4">
                        <SizableText color="$colorMuted">No affiliates yet.</SizableText>
                    </View>
                )}

                {filtered.map(a => (
                    <XStack
                        key={a.affiliateId}
                        paddingHorizontal="$4"
                        paddingVertical="$3"
                        gap="$3"
                        alignItems="center"
                        borderBottomWidth={1}
                        borderBottomColor="$borderColor"
                        cursor="pointer"
                        hoverStyle={{ backgroundColor: '$backgroundHover' }}
                        onPress={() => navigate(a.affiliateId)}
                    >
                        <YStack flex={2} minWidth={0}>
                            <SizableText fontWeight="600">{a.name}</SizableText>
                            <SizableText size="$1" color="$colorMuted">{a.email}</SizableText>
                        </YStack>
                        <SizableText flex={1.5} fontFamily="$mono" display="none" $sm={{ display: 'flex' }}>{a.promoCode}</SizableText>
                        <SizableText flex={1} display="none" $sm={{ display: 'flex' }}>
                            {a.referralCount} ({a.activeReferralCount} active)
                        </SizableText>
                        <SizableText flex={1.3}>{fmtAud(a.paidToDateCents)}</SizableText>
                        <SizableText flex={1.3} fontWeight="600">{fmtAud(a.unpaidCents)}</SizableText>
                        <View flex={1.2}>
                            <Badge tone={affiliateStatusTone[a.status] ?? 'neutral'}>{a.status}</Badge>
                            {a.payoutFailed ? (
                                <SizableText
                                    fontSize={11}
                                    color="$statusDanger"
                                >
                                    payout failed
                                </SizableText>
                            ) : !a.connectOnboardingComplete ? (
                                <SizableText
                                    fontSize={11}
                                    color="$statusWarn"
                                >
                                    payouts not set up
                                </SizableText>
                            ) : null}
                        </View>
                    </XStack>
                ))}
            </Card>

            {creating && (
                <EditAffiliateModal
                    open={creating}
                    // Refetch on close too: an affiliate created then dismissed via the backdrop/Esc
                    // (rather than the "Done" button) would otherwise be missing until a reload.
                    onClose={() => {
                        setCreating(false)
                        query.refetch()
                    }}
                    onSaved={() => {
                        setCreating(false)
                        query.refetch()
                    }}
                />
            )}
        </YStack>
    )
}
